import {useEffect, useRef} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {Outlet, useNavigate} from 'react-router-dom';
import {useAppDispatch} from '../../store/Store';
import {
  setAuthToken,
  setUserEmailId,
  setUserName,
  setUserRole,
  setCategoryList,
  setSectorList,
  clearAuthStore,
  setImpersonatedEmail,
} from '../../store/AuthSlice';
import {toRelativeUrl} from '@okta/okta-auth-js';
import {clearAadToken, clearPowerBiStore} from '../../store/PowerBiSlice';
import {resetSubmission, setAuthTime} from '../../store/NpsSlice';
import appInsights from '../../services/analytics/appInsights';
import {FullStory} from '@fullstory/browser';
import {
  logPowerBiUserEvent,
  LOGIN_LANDING_REPORT_ID,
  LOGIN_LANDING_SUBREPORT_NAME,
} from '../../Utils/Apis/powerBiEventLogApi';
import {SUPER_USER_OVERRIDE_EMAILS} from '../../constants/TargetUsers';
export const PrivateRoutes = () => {
  const {oktaAuth, authState} = useOktaAuth();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const lastTrackedUserId = useRef<string | null>(null);

  //   oktaAuth.tokenManager.on('renewed', function (key, newToken, oldToken) {
  //     if (newToken?.idToken) {
  //       dispatch(setAuthToken(newToken?.idToken));
  //     }
  //   });

  useEffect(() => {
    if (authState && authState?.isAuthenticated && authState?.idToken) {
      const claims = authState.idToken.claims;
      const currentUserId = String(claims?.gpid || claims?.sub);
      if (currentUserId && currentUserId !== lastTrackedUserId.current) {
        const userEmail = String(claims?.email || claims?.sub || '');
        appInsights.setAuthenticatedUserContext(currentUserId, userEmail);
        FullStory('setIdentity', {
          uid: currentUserId,
          properties: {
            displayName: String(claims?.name || userEmail),
            email: userEmail,
          },
        });
        const sessionUrl = FullStory('getSession');
        if (sessionUrl) {
          appInsights.trackTrace({
            message: 'FullStory Session Link',
            properties: {fullStoryUrl: sessionUrl},
          });
        }
        logPowerBiUserEvent(
          userEmail,
          LOGIN_LANDING_REPORT_ID,
          LOGIN_LANDING_SUBREPORT_NAME,
        );
        lastTrackedUserId.current = currentUserId;
      }
    }
    return () => {
      // 5. Clear the user context
      console.log('clearing the App Insights context clear');
      appInsights.clearAuthenticatedUserContext();
      FullStory('setIdentity', {anonymous: true});
      lastTrackedUserId.current = null;
    };
  }, [authState]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!authState) {
        return;
      }

      if (!authState?.isAuthenticated) {
        sessionStorage.clear();
        localStorage.clear();
        oktaAuth.tokenManager.clear();

        try {
          // Check if Okta session exists (without prompting login)
          const session = await oktaAuth.session.get();
          if (!session) {
            // No active session at Okta → show signup
            navigate('/signup');
            return;
          }

          // Try silent token retrieval if session exists
          try {
            const {tokens} = await oktaAuth.token.getWithoutPrompt({
              responseType: ['id_token', 'token'],
              scopes: [
                'openid',
                'profile',
                'email',
                'pepappspendwiseroles',
                'pepappspendwisesectors',
                'pepappspendwisecategories',
                'pepappspendwiseuseremail',
                'pepappspendwiseimpgpid',
                'offline_access',
              ],
            });
            oktaAuth.tokenManager.setTokens(tokens);
            return; // Logged in successfully
          } catch (silentErr: any) {
            console.log('Silent SSO failed:', silentErr);

            // If they have a session but are not assigned → force login so Okta shows error
            if (
              silentErr.errorCode === 'access_denied' ||
              silentErr.message?.includes('User is not assigned')
            ) {
              oktaAuth.token.getWithRedirect({
                responseType: ['id_token', 'token'],
                scopes: [
                  'openid',
                  'profile',
                  'email',
                  'pepappspendwiseroles',
                  'pepappspendwisesectors',
                  'pepappspendwisecategories',
                  'pepappspendwiseuseremail',
                  'pepappspendwiseimpgpid',
                  'offline_access',
                ],
                prompt: 'login', // Forces Okta's error page to show
              });
              return;
            }

            // Fallback if some other error occurs
            navigate('/signup');
          }
        } catch (err) {
          console.error('Session check failed:', err);
          navigate('/signup');
        }
      } else {
        let userRoles = authState.accessToken?.claims.pepappspendwiseroles;
        const categoryList =
          authState.accessToken?.claims.pepappspendwisecategories;
        const sectorList = authState.accessToken?.claims.pepappspendwisesectors;
        const userEmail = authState.idToken?.claims.email;
        if (
          userEmail &&
          SUPER_USER_OVERRIDE_EMAILS.includes(userEmail.toLowerCase())
        ) {
          userRoles = ['Super User'];
        }
        const oktaImpersonatedEmail =
          authState.accessToken?.claims.pepappspendwiseuseremail;
        dispatch(setAuthToken(authState.accessToken?.accessToken));
        dispatch(setUserName(authState.idToken?.claims.name));
        dispatch(setUserEmailId(authState.idToken?.claims.email));
        dispatch(setUserRole(userRoles));

        const isImpersonationUser =
          Array.isArray(userRoles) && userRoles.includes('Impersonation User');
        dispatch(
          setImpersonatedEmail(
            isImpersonationUser ? oktaImpersonatedEmail : null,
          ),
        );
        if (userRoles) {
          if (Array.isArray(userRoles)) {
            const firstRole = userRoles[userRoles?.length - 1];
            if (
              firstRole &&
              typeof firstRole === 'string' &&
              ['Sector User', 'Category User'].includes(firstRole)
            ) {
              if (firstRole == 'Sector User') {
                dispatch(setSectorList(sectorList));
              } else if (firstRole == 'Category User') {
                dispatch(setCategoryList(categoryList));
                dispatch(setSectorList(sectorList));
              }
            } else {
              dispatch(setCategoryList(['']));
              dispatch(setSectorList(['']));
            }
          }
        }
      }
    };
    checkAuth();
  }, [oktaAuth, authState?.isAuthenticated, authState, dispatch]);

  if (!authState) {
    return <div>Loading...</div>;
  }
  if (!authState?.isAuthenticated) {
    return <div>Redirecting to signin...</div>;
  }

  return <Outlet />;
};
